import {
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
} from 'antd';
import {
  FORMAT_OPTIONS,
  PRE_SUFFIX,
  PRE_SUFFIX_OPTIONS,
  PreSuffix,
  SemanticRuleType,
} from './const';
import { Field } from './type';

// 语义
export interface SemanticFormType {
  tag_code: string;
  type: SemanticRuleType;
  regex_expression: string;
  pre_text: { type: 'TEXT'; values: string[] };
  post_text: { type: 'TEXT'; values: string[] };
  prefix: {
    type: PreSuffix;
    values: string[];
  };
  suffix: {
    type: PreSuffix;
    values: string[];
  };
  contain: {
    type: 'TEXT';
    values: string[];
  };
  not_contain: {
    type: 'TEXT';
    values: string[];
  };
  min_len: number | null;
  max_len: number | null;
  greedy: boolean;
  _extra?: Record<string, any>;
}

const PreSuffixFormItem: React.FC<{
  onChange?: (value: SemanticFormType['prefix']) => void;
  value?: SemanticFormType['prefix'];
}> = ({ onChange, value }) => {
  const handleChange = (newSelectValue: string, newInputValue: string[]) => {
    onChange?.({
      type: newSelectValue as PreSuffix,
      values: newInputValue,
    });
  };

  return (
    <Space.Compact className="w-full">
      <Select
        className="!w-[120px]"
        options={PRE_SUFFIX_OPTIONS}
        value={value?.type || ''}
        onChange={(newValue) => {
          handleChange(newValue, []);
        }}
      />
      {value?.type === 'FORMAT' && (
        <Select
          className="w-full"
          options={FORMAT_OPTIONS}
          value={value?.values}
          onChange={(newValue) => {
            if (!newValue) return handleChange(value.type, []);
            handleChange(value.type, [newValue as unknown as string]);
          }}
        />
      )}
      {value?.type === 'TEXT' && (
        <Input
          placeholder="请输入前缀"
          className="w-full"
          value={value?.values?.[0]}
          onChange={(e) => {
            if (!e.target.value) return handleChange(value.type, []);
            handleChange(value.type, [e.target.value]);
          }}
        />
      )}
    </Space.Compact>
  );
};
const SuffixFormItem: React.FC<{
  onChange?: (value: SemanticFormType['suffix']) => void;
  value?: SemanticFormType['suffix'];
}> = ({ onChange, value }) => {
  const handleChange = (newSelectValue: string, newInputValue: string[]) => {
    onChange?.({
      type: newSelectValue as PreSuffix,
      values: newInputValue,
    });
  };

  return (
    <Space.Compact className="w-full">
      <Select
        getPopupContainer={(el) => el.parentElement}
        className="!w-[120px]"
        options={PRE_SUFFIX_OPTIONS}
        value={value?.type || ''}
        onChange={(newValue) => {
          handleChange(newValue, []);
        }}
      />
      {value?.type === 'FORMAT' && (
        <Select
          getPopupContainer={(el) => el.parentElement}
          className="w-full"
          options={FORMAT_OPTIONS}
          value={value.values}
          onChange={(newValue) => {
            if (!newValue) return handleChange(value.type, []);
            handleChange(value.type, [newValue as unknown as string]);
          }}
        />
      )}
      {value?.type === 'TEXT' && (
        <Input
          placeholder="请输入后缀"
          className="w-full"
          value={value.values}
          onChange={(e) => {
            if (!e.target.value) return handleChange(value.type, []);
            handleChange(value.type, [e.target.value]);
          }}
        />
      )}
    </Space.Compact>
  );
};

export const SemanticForm: React.FC<{
  field: Field;
  form: FormInstance;
  prefix: (string | number)[];
}> = ({ field, form, prefix }) => (
  <div className="bg-[#F9FAFC] border border-solid border-[#E5E7EC] p-3 rounded-md">
    <Form.Item
      label="上文"
      tooltip="请输入抽取内容可能的上文参考内容"
      name={[field.name, 'pre_text']}
      initialValue={{ type: 'TEXT', values: [] }}
    
      rules={[
        {
          // required: true,
          // message: '上文和前缀至少有一个不为空',
          validator(rule, value) {
            // 判断上文和前缀至少有一个不为空
            const prefixValue: SemanticFormType['prefix'] = form.getFieldValue([
              ...prefix,
              'prefix',
            ]);

            if (!value?.values?.length && !prefixValue.values?.length) {
              return Promise.reject('上文和前缀至少有一个不为空');
            }
            return Promise.resolve();
          },
          warningOnly: true,
        },
      ]}
      normalize={(tags: string[]) => {
        const formatValue = {
          type: 'TEXT',
          values: tags,
        };
        // 过滤tag的长度不能超过32
        // formatValue.values = formatValue.values.filter(
        //   (tag) => tag.length <= 32,
        // );

        return formatValue;
      }}
      getValueProps={(value: SemanticFormType['pre_text']) => {
        return {
          value: value?.values,
        };
      }}
    >
      <Select
        placeholder="请输入上文"
        maxTagTextLength={20}
        getPopupContainer={(el) => el.parentElement}
        // defaultActiveFirstOption={false}
        mode="tags"
      />
    </Form.Item>
    <Form.Item
      tooltip="请输入抽取内容可能的下文参考内容"
      label="下文"
      initialValue={{ type: 'TEXT', values: [] }}
      name={[field.name, 'post_text']}
      rules={[
        {
          validator(rule, value) {
            // 判断上文和前缀至少有一个不为空
            const suffixValue: SemanticFormType['suffix'] = form.getFieldValue([
              ...prefix,
              'suffix',
            ]);

            if (!value?.values?.length && !suffixValue.values?.length) {
              return Promise.reject('下文和后缀至少有一个不为空');
            }
            return Promise.resolve();
          },
          warningOnly: true,
        },
      ]}
      // TODO: 对于这种需要格式化数据的可以直接写个组件
      normalize={(tags: string[]) => {
        const formatValue = {
          type: 'TEXT',
          values: tags,
        };

        // 过滤tag的长度不能超过32
        //         formatValue.values = formatValue.values.filter(
        //           (tag) => tag.length <= 32,
        //         );
        //
        return formatValue;
      }}
      getValueProps={(value: SemanticFormType['post_text']) => {
        return {
          value: value?.values,
        };
      }}
    >
      <Select
        placeholder="请输入下文"
        maxTagTextLength={20}
        getPopupContainer={(el) => el.parentElement}
        // defaultActiveFirstOption={false}
        mode="tags"
        allowClear
      />
    </Form.Item>
    <Form.Item
      label="前缀"
      initialValue={{ type: PRE_SUFFIX.TEXT, values: [] }}
      name={[field.name, 'prefix']}
      // rules={[{ required: true, message: '前缀和后缀至少有一个不为空' }]}
    >
      <PreSuffixFormItem />
    </Form.Item>
    <Form.Item
      label="后缀"
      name={[field.name, 'suffix']}
      initialValue={{ type: PRE_SUFFIX.TEXT, values: [] }}
      // rules={[{ required: true, message: '前缀和后缀至少有一个不为空' }]}
    >
      <SuffixFormItem />
    </Form.Item>
    <Form.Item
      normalize={(value) => {
        const format: SemanticFormType['contain'] = {
          type: 'TEXT',
          values: [value],
        };
        return format;
      }}
      getValueProps={(value: SemanticFormType['contain']) => {
        return {
          value: value?.values?.[0],
        };
      }}
      label="必须包含的内容"
      name={[field.name, 'contain']}
    >
      <Input placeholder="请输入必须包含的内容" />
    </Form.Item>
    <Form.Item
      normalize={(value) => {
        const format: SemanticFormType['not_contain'] = {
          type: 'TEXT',
          values: [value],
        };
        return format;
      }}
      getValueProps={(value: SemanticFormType['not_contain']) => {
        return {
          value: value?.values?.[0],
        };
      }}
      label="一定不包含的内容"
      name={[field.name, 'not_contain']}
    >
      <Input placeholder="请输入一定不包含的内容" />
    </Form.Item>
    <Form.Item label="最小长度" name={[field.name, 'min_len']} initialValue={0}>
      <InputNumber min={0} placeholder="请输入最小长度" />
    </Form.Item>
    <Form.Item
      label="最大长度"
      name={[field.name, 'max_len']}
      initialValue={10}
    >
      <InputNumber min={0} max={99999} placeholder="请输入最大长度" />
    </Form.Item>
    <Form.Item
      label="是否贪婪"
      name={[field.name, 'greedy']}
      initialValue={true}
    >
      <Radio.Group>
        <Radio value={true}>是</Radio>
        <Radio value={false}>否</Radio>
      </Radio.Group>
    </Form.Item>
  </div>
);
